import { Flex, Table } from "antd"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import useCampaignColumns from "../hooks/useCampaignColumns"
import { Key, useEffect, useMemo, useState } from "react"
import { ORDER_TYPES } from "../constants"
import { useActions } from "../../../hooks/useActions"
import PlacementsTable from "./PlacementsTable"
import { ICampaignDataColumns } from "../../sponsoredProducts/types/table"
import { dataRepresentationEnum } from "../../../types/customers"
import { placementsEnum, weekFilterEnum } from "../types/types"
import { IPlacementDataForUpdate } from "../store/types/placements"
import usePlacementMetrics from "../hooks/usePlacementMetrics"
import { IPlacementDataTable } from "../types/placement"
import { ICampaign } from "../types/sponsoredProducts"

interface CampaignTableProps {
  campaigns: ICampaign[]
  isExpandedAll: boolean
  setSelectedCampaignIds: React.Dispatch<React.SetStateAction<number[]>>
  selectedCampaignIds: number[]
  placementDataForUpdate: IPlacementDataForUpdate[]
  setPlacementsDataForUpdate: React.Dispatch<React.SetStateAction<IPlacementDataForUpdate[]>>
  minLimitedCPCByCampaignIdMap: Record<string, number>
  setMinLimitedCPCByCampaignIdMap: React.Dispatch<React.SetStateAction<Record<string, number>>>
  refetchCampaigns: () => void
}

const CampaignTable: React.FC<CampaignTableProps> = ({
  campaigns,
  isExpandedAll,
  setSelectedCampaignIds,
  selectedCampaignIds,
  placementDataForUpdate,
  setPlacementsDataForUpdate,
  minLimitedCPCByCampaignIdMap,
  setMinLimitedCPCByCampaignIdMap,
  refetchCampaigns,
}) => {
  const { dateRange, isOrder } = useTypedSelector((select) => select.filter)
  const { campaignData, isLoading } = useTypedSelector((select) => select.sponsoredProductsSKC)
  const [filteredData, setFilteredData] = useState<ICampaignDataColumns[]>([])

  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([])

  const {
    calcNewBidAdjustmentForPlacement,
    selectCampaignIfNeeded,
    calcMetricsBeforeUpdatedAcos,
    capLimitedCPC,
  } = usePlacementMetrics({
    setPlacementsDataForUpdate,
    setSelectedCampaignIds,
    setMinLimitedCPCByCampaignIdMap,
  })

  const { fetchPlacements } = useActions()
  const columns = useCampaignColumns({ minLimitedCPCByCampaignIdMap, refetchCampaigns })

  const campaignsDataTable = useMemo(() => {
    return campaigns
      ? campaigns.map((campaign) => {
          const newBid = minLimitedCPCByCampaignIdMap[String(campaign.campaignId)]
          return {
            ...campaign,
            newBidAdjustment: newBid || newBid === 0 ? `$${newBid.toFixed(2)}` : "-",
          }
        })
      : []
  }, [campaigns, minLimitedCPCByCampaignIdMap])

  useEffect(() => {
    if (isExpandedAll) {
      const campaignIds = filteredData.length
        ? filteredData.map((campaign) => campaign.campaignId)
        : campaigns.map((campaign) => campaign.campaignId)

      setExpandedRowKeys(campaignIds)
    } else {
      setExpandedRowKeys([])
    }
  }, [isExpandedAll])

  function ownCampaignIds(): number[] {
    return campaigns.map((campaign) => campaign.campaignId)
  }

  function handleRowSelectionChange(selectedRowKeys: Key[]) {
    const ownIds = ownCampaignIds()
    setSelectedCampaignIds((prev) => [
      ...prev.filter((id) => !ownIds.includes(id)),
      ...selectedRowKeys.map((key) => Number(key)),
    ])
  }

  function handleOnExpand(expanded: boolean, record: ICampaignDataColumns) {
    if (expanded && !isExpandedAll) {
      fetchPlacementsData([record.campaignId])
      setExpandedRowKeys([...expandedRowKeys, record.campaignId])
      return
    }

    if (!expanded) {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.campaignId))
    }
  }

  function fetchPlacementsData(campaignIds: number[]) {
    fetchPlacements({
      name: dataRepresentationEnum.PLACEMENT,
      type: weekFilterEnum.DAY,
      campaignNames: campaignIds,
      fromDate: dateRange.from,
      toDate: dateRange.to,
      filterIds: [],
      filterType: isOrder ? ORDER_TYPES.ORDERS : ORDER_TYPES.DATE,
    })
  }

  function handleLimitedCPCChange(placement: IPlacementDataTable, enteredValue: number) {
    const value = capLimitedCPC(placement.campaignId, enteredValue)
    let minLimitedCPC = Number.POSITIVE_INFINITY
    let maxLimitedCPC = Number.NEGATIVE_INFINITY

    const placementsDataTable: IPlacementDataForUpdate | undefined = placementDataForUpdate.find(
      (placementDataForUpdate) => placementDataForUpdate.campaignId === placement.campaignId
    )
    let placementsWithChanged: IPlacementDataTable[] =
      placementsDataTable?.placements.map((placementDataTable) => {
        const isCurrentPlacement = placementDataTable.placement === placement.placement
        const nextPlacement = isCurrentPlacement
          ? { ...placementDataTable, limitedCPC: value }
          : placementDataTable

        if (
          nextPlacement.placement !== placementsEnum.AMAZON_BUSINESS &&
          nextPlacement.placement !== placementsEnum.AUDIENCE &&
          (nextPlacement.limitedCPC || 0) > maxLimitedCPC
        ) {
          maxLimitedCPC = nextPlacement.limitedCPC || 0
        }

        if ((nextPlacement.limitedCPC || 0) < minLimitedCPC) {
          minLimitedCPC = nextPlacement.limitedCPC || 0
        }

        return nextPlacement
      }) || []

    if (placementsWithChanged.length === 0) {
      return
    }

    setMinLimitedCPCByCampaignIdMap((newBidByCampaignIdMap) => {
      return {
        ...newBidByCampaignIdMap,
        [String(placement.campaignId)]: minLimitedCPC,
      }
    })

    placementsWithChanged = calcNewBidAdjustmentForPlacement(placementsWithChanged, minLimitedCPC)
    updatePlacementDataForUpdate(placement.campaignId, placementsWithChanged)
    selectCampaignIfNeeded({
      campaignId: placement.campaignId,
      placements: placementsWithChanged,
      campaignData,
      setSelectedCampaignIds,
    })
  }

  function updatePlacementDataForUpdate(
    campaignId: number,
    placementsWithChanged: IPlacementDataTable[]
  ): void {
    setPlacementsDataForUpdate((prev) => {
      return prev.map((placementDataForUpdate) => {
        if (placementDataForUpdate.campaignId === campaignId) {
          return {
            campaignId: placementDataForUpdate.campaignId,
            placements: placementsWithChanged,
          }
        }

        return placementDataForUpdate
      })
    })
  }

  function handleTargetAcosChange(placement: IPlacementDataTable, value: number): void {
    setPlacementsDataForUpdate((prev) => {
      return prev.map((placementDataForUpdate) => {
        if (placementDataForUpdate.campaignId === placement.campaignId) {
          const placementsWithChangedTargetAcos = placementDataForUpdate.placements.map(
            (placementForUpdate) => {
              if (placementForUpdate.placement === placement.placement) {
                return {
                  ...placementForUpdate,
                  targetAcos: value,
                }
              }

              return placementForUpdate
            }
          )
          const placementWithUpdatedMetrics = calcMetricsBeforeUpdatedAcos(
            placementsWithChangedTargetAcos,
            placement.campaignId
          )

          return {
            campaignId: placementDataForUpdate.campaignId,
            placements: placementWithUpdatedMetrics,
          }
        }

        return placementDataForUpdate
      })
    })
  }

  function findPlacement(campaignId: number): IPlacementDataTable[] {
    const foundPlacement = placementDataForUpdate.find(
      (placementDataForUpdate) => placementDataForUpdate.campaignId === campaignId
    )
    return foundPlacement?.placements || []
  }

  function onSelectAll(selected: boolean) {
    const ownIds = ownCampaignIds()
    setSelectedCampaignIds((prev) => {
      const otherGroupIds = prev.filter((id) => !ownIds.includes(id))
      return selected
        ? [...otherGroupIds, ...filteredData.map((row) => row.campaignId)]
        : otherGroupIds
    })
  }

  function handleChangeCampaigns(pagination, filters, sorter, extra) {
    setFilteredData(extra.currentDataSource)
  }

  return (
    <Flex vertical gap={10}>
      <Table
        columns={columns}
        dataSource={campaignsDataTable as any}
        loading={isLoading}
        rowKey="campaignId"
        scroll={{ x: "max-content", y: window.innerHeight }}
        pagination={false}
        virtual={true}
        onChange={handleChangeCampaigns}
        expandable={{
          onExpand: handleOnExpand,
          expandedRowRender: (record) => (
            <PlacementsTable
              placementsDataTable={findPlacement(record.campaignId)}
              handleLimitedCPCChange={handleLimitedCPCChange}
              handleTargetAcosChange={handleTargetAcosChange}
              setPlacementsDataForUpdate={setPlacementsDataForUpdate}
            />
          ),
          expandedRowKeys: expandedRowKeys,
          columnWidth: 48,
        }}
        rowSelection={{
          selectedRowKeys: selectedCampaignIds,
          onChange: handleRowSelectionChange,
          onSelectAll: onSelectAll,
          columnWidth: 48,
        }}
      />
    </Flex>
  )
}

export default CampaignTable
